// A small ZIP reader for Strava's account export (and gunzip for its .fit.gz, .gpx.gz, .tcx.gz files), on the
// platform's own DecompressionStream (browsers and Node 18+), so no package is needed. Reads the central
// directory from the end of the file and each entry by its offset, through Blob.slice, so a large export
// (photos and all) never has to be read into memory as a whole. ZIP64 and encrypted archives are refused.
import { TrackFileError } from './gpx';

/** The part of a Blob (or File) the reader needs. */
export interface BlobLike {
  size: number;
  slice(start?: number, end?: number): { arrayBuffer(): Promise<ArrayBuffer> };
}

export interface ZipEntry {
  name: string;
  method: number;
  compressedSize: number;
  size: number;
  offset: number;
}

/** Largest unpacked file (a GPX, TCX or FIT file, the same as the readers' own limit). */
export const MAX_UNPACKED_BYTES = 25_000_000;
/** Largest total unpacked from one ZIP (years of Strava activities). */
export const MAX_ZIP_TOTAL_BYTES = 2_000_000_000;

/**
 * Unpacks with a running size check: reading stops (and the stream is cancelled) as soon as the output passes
 * `maxBytes`, so a small "zip bomb" cannot fill the memory.
 */
async function decompress(bytes: Uint8Array, format: 'deflate-raw' | 'gzip', maxBytes: number): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') throw new TrackFileError('This browser cannot unpack compressed files. Please use a current browser.');
  const reader = new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(new DecompressionStream(format)).getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch {
      throw new TrackFileError('This compressed file is damaged.');
    }
    if (chunk.done) break;
    total += chunk.value.length;
    if (total > maxBytes) {
      await reader.cancel().catch(() => undefined);
      throw new TrackFileError(`This file unpacks to more than ${Math.round(maxBytes / 1_000_000)} MB, which is more than an activity file can be.`);
    }
    parts.push(chunk.value);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}

export const gunzip = (bytes: Uint8Array, maxBytes = MAX_UNPACKED_BYTES): Promise<Uint8Array> => decompress(bytes, 'gzip', maxBytes);

const read = async (blob: BlobLike, start: number, end: number) => new Uint8Array(await blob.slice(start, end).arrayBuffer());

/** Lists the files in a ZIP archive. */
export async function zipEntries(blob: BlobLike): Promise<ZipEntry[]> {
  // The end-of-central-directory record is in the last 22 bytes plus up to 64 KB of comment.
  const tailStart = Math.max(0, blob.size - 22 - 65_535);
  const tail = await read(blob, tailStart, blob.size);
  const tv = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
  let eocd = -1;
  for (let i = tail.length - 22; i >= 0; i--) if (tv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new TrackFileError('This is not a ZIP file.');
  const count = tv.getUint16(eocd + 10, true);
  const cdSize = tv.getUint32(eocd + 12, true);
  const cdOffset = tv.getUint32(eocd + 16, true);
  if (count === 0xffff || cdOffset === 0xffffffff) throw new TrackFileError('This ZIP file is too large to read here (ZIP64). Unpack it and choose the activity files instead.');
  const cd = await read(blob, cdOffset, cdOffset + cdSize);
  const v = new DataView(cd.buffer, cd.byteOffset, cd.byteLength);
  const entries: ZipEntry[] = [];
  const decoder = new TextDecoder();
  let at = 0;
  for (let i = 0; i < count; i++) {
    if (at + 46 > cd.length || v.getUint32(at, true) !== 0x02014b50) throw new TrackFileError('This ZIP file is damaged.');
    const flags = v.getUint16(at + 8, true);
    const method = v.getUint16(at + 10, true);
    const compressedSize = v.getUint32(at + 20, true);
    const size = v.getUint32(at + 24, true);
    const nameLen = v.getUint16(at + 28, true);
    const extraLen = v.getUint16(at + 30, true);
    const commentLen = v.getUint16(at + 32, true);
    const offset = v.getUint32(at + 42, true);
    const name = decoder.decode(cd.subarray(at + 46, at + 46 + nameLen));
    if (!(flags & 1) && !name.endsWith('/')) entries.push({ name, method, compressedSize, size, offset });
    at += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

/** The bytes of one entry, unpacked, at most `maxBytes` (whatever the entry claims about its size). */
export async function zipRead(blob: BlobLike, e: ZipEntry, maxBytes = MAX_UNPACKED_BYTES): Promise<Uint8Array> {
  if (e.size > maxBytes || e.compressedSize > maxBytes) throw new TrackFileError(`${e.name} is larger than ${Math.round(maxBytes / 1_000_000)} MB.`);
  const head = await read(blob, e.offset, e.offset + 30);
  const hv = new DataView(head.buffer, head.byteOffset, head.byteLength);
  if (hv.getUint32(0, true) !== 0x04034b50) throw new TrackFileError(`The ZIP entry ${e.name} is damaged.`);
  const start = e.offset + 30 + hv.getUint16(26, true) + hv.getUint16(28, true);
  const data = await read(blob, start, start + e.compressedSize);
  if (e.method === 0) return data;
  if (e.method === 8) return decompress(data, 'deflate-raw', maxBytes);
  throw new TrackFileError(`The ZIP entry ${e.name} uses a compression this app cannot read.`);
}
